// dailyModulesCompleted.js - Modal content for Daily Modules Completed KPI
// Place at: src/components/modalContent/productionMilestonesModalComponents/dailyModulesCompleted.js

import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import useDailyModulesCompletedData from "../../../hooks/useDailyModulesCompletedData";
import useTableSort from "../../../hooks/useTableSort";
import SortableTableHeader from "../../modalContent/SortableTableHeader";

const barColor = (status) => {
  if (status === "Met") return "#38a169";
  if (status === "Near") return "#d69e2e";
  if (status === "Future") return "#cbd5e0";
  return "#cc0000"; // Missed
};

const statusLabel = (status) => {
  if (status === "Met") return "Met/Exceeded";
  if (status === "Near") return "Near";
  if (status === "Future") return "—";
  return "Missed";
};

const statusClass = (status) => {
  if (status === "Met") return "pml-variance-cell--pos";
  if (status === "Near") return "";
  if (status === "Future") return "";
  return "pml-variance-cell--neg"; // Missed
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  // Future days have no actual data
  if (d.actual == null) {
    return (
      <div className="pml-chart-tooltip">
        <div className="pml-chart-tooltip-date">Day {label}</div>
        <div className="pml-chart-tooltip-row">
          <div
            className="pml-chart-tooltip-dot"
            style={{ background: "#cbd5e0" }}
          />
          Future
        </div>
      </div>
    );
  }
  return (
    <div className="pml-chart-tooltip">
      <div className="pml-chart-tooltip-date">Day {label}</div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: barColor(d.status) }}
        />
        {d.actual} actual / {d.target} target
        {d.pct != null ? ` (${d.pct.toFixed(1)}%)` : ""}
      </div>
    </div>
  );
};

const DailyModulesCompleted = ({ goals }) => {
  const { metrics, graph, table, loading, error } =
    useDailyModulesCompletedData();

  // ── Table sorting — must be called before any early returns ───────────────
  const { sortedData, requestSort, getSortClass } = useTableSort(table, {
    initialKey: "day",
    initialDirection: "desc",
  });

  // ── Loading / error states ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="pml-modal-flex-content pml-modal-loading">
        Loading daily modules completed data…
      </div>
    );
  }

  if (error) {
    return (
      <div className="pml-modal-flex-content pml-modal-error">{error}</div>
    );
  }

  // goals prop allows the parent to override the live target if needed;
  // falls back to the server-derived dailyTarget from the hook.
  const target = goals?.dailyModulesCompleted || metrics.dailyTarget;

  const { monthAvgPct, daysMet, daysNear, daysMissed } = metrics;

  // ── Month totals (elapsed days only) ─────────────────────────────────────
  const elapsedRows = table.filter((r) => r.actual != null);
  const totalActual = elapsedRows.reduce((s, r) => s + r.actual, 0);
  const totalTarget = elapsedRows.reduce((s, r) => s + r.target, 0);
  const overallPct =
    totalTarget > 0 ? ((totalActual / totalTarget) * 100).toFixed(1) : null;

  return (
    <div className="pml-modal-flex-content">
      {/* Stat band */}
      <div className="pml-modal-stat-band pml-modal-stat-band--4">
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Month Avg %</span>
          <span
            className={`pml-modal-stat-value ${
              monthAvgPct >= target * 100
                ? "pml-modal-stat-value--ok"
                : monthAvgPct >= target * 90
                  ? "pml-modal-stat-value--amber"
                  : "pml-modal-stat-value--alert"
            }`}
          >
            {monthAvgPct}%
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Days Met/Exceeded (100%)</span>
          <span className="pml-modal-stat-value pml-modal-stat-value--ok">
            {daysMet}
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Days Near (75–99%)</span>
          <span className="pml-modal-stat-value pml-modal-stat-value--amber">
            {daysNear}
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Days Missed (&lt;75%)</span>
          <span
            className={`pml-modal-stat-value ${daysMissed > 0 ? "pml-modal-stat-value--alert" : "pml-modal-stat-value--ok"}`}
          >
            {daysMissed}
          </span>
        </div>
      </div>

      {/* Line chart — daily completion this month */}
      <p className="pml-modal-sub-title">Daily Completion - This Month</p>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={graph}
            margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e2e8f0"
              vertical={false}
            />
            <XAxis
              dataKey="day"
              tick={{ fontSize: 10, fill: "#718096" }}
              tickLine={false}
              axisLine={{ stroke: "#e2e8f0" }}
              interval={2}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={false}
              width={38}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={target}
              stroke="#1a1a1a"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              label={{
                value: `Target ${target}`,
                position: "insideTopRight",
                fontSize: 11,
                fill: "#1a1a1a",
              }}
            />
            <Line
              type="monotone"
              dataKey="actual"
              stroke="#a0aec0"
              strokeWidth={2}
              connectNulls={false}
              isAnimationActive={false}
              dot={(props) => {
                const { cx, cy, payload, index } = props;
                if (payload.actual == null) {
                  return (
                    <circle
                      key={`dot-${index}`}
                      cx={cx}
                      cy={cy}
                      r={3}
                      fill="#cbd5e0"
                      fillOpacity={0.4}
                      stroke="none"
                    />
                  );
                }
                const fill =
                  payload.actual >= target
                    ? "#38a169"
                    : payload.actual >= target * 0.75
                      ? "#d69e2e"
                      : "#cc0000";
                return (
                  <circle
                    key={`dot-${index}`}
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill={fill}
                    fillOpacity={0.85}
                    stroke="#fff"
                    strokeWidth={1}
                  />
                );
              }}
              activeDot={(props) => {
                const { cx, cy, payload, index } = props;
                const fill =
                  payload.actual == null
                    ? "#cbd5e0"
                    : payload.actual >= target
                      ? "#38a169"
                      : payload.actual >= target * 0.75
                        ? "#d69e2e"
                        : "#cc0000";
                return (
                  <circle
                    key={`active-dot-${index}`}
                    cx={cx}
                    cy={cy}
                    r={6}
                    fill={fill}
                    fillOpacity={0.95}
                    stroke="#fff"
                    strokeWidth={2}
                  />
                );
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Daily log table */}
      <p className="pml-modal-sub-title">Daily Log</p>
      <div className="pml-modal-scroll-wrap pml-table-auto-height">
        <table className="pml-modal-table">
          <thead>
            <tr>
              <SortableTableHeader
                columnKey="day"
                onSort={requestSort}
                sortClass={getSortClass("day")}
              >
                Date
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="target"
                onSort={requestSort}
                sortClass={getSortClass("target")}
                style={{ textAlign: "center" }}
              >
                Target
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="actual"
                onSort={requestSort}
                sortClass={getSortClass("actual")}
                style={{ textAlign: "center" }}
              >
                Actual
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="pct"
                onSort={requestSort}
                sortClass={getSortClass("pct")}
                style={{ textAlign: "center" }}
              >
                % Complete
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="status"
                onSort={requestSort}
                sortClass={getSortClass("status")}
                style={{ textAlign: "center" }}
              >
                Status
              </SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, i) => (
              <tr key={i}>
                <td>{row.date || `Day ${row.day}`}</td>
                <td style={{ textAlign: "center" }}>{row.target}</td>
                <td style={{ textAlign: "center", fontWeight: 700 }}>
                  {row.actual ?? "—"}
                </td>
                <td
                  style={{
                    textAlign: "center",
                    fontWeight: 700,
                    color: row.pct == null ? "#a0aec0" : barColor(row.status),
                  }}
                >
                  {row.pct != null ? `${row.pct.toFixed(1)}%` : "—"}
                </td>
                <td
                  style={{ textAlign: "center" }}
                  className={statusClass(row.status)}
                >
                  {statusLabel(row.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer — elapsed-day totals only */}
      {overallPct != null && (
        <div style={{ fontSize: 12, color: "#a0aec0" }}>
          Month to date: {totalActual} actual modules / {totalTarget} target (
          {overallPct}% overall)
        </div>
      )}
    </div>
  );
};

export default DailyModulesCompleted;
